import SidePanel from '../SidePanel.jsx';
import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import * as Toast from '../../lib/Toast.js';
import { useEffect, useState } from 'react';
import ClientPicker from '../ClientPicker.jsx';
import ErrorMessageBlock from '../ErrorMessageBlock.jsx';
import Loading from '../Loading';
import {
    usePackageDetailsQuery,
    usePackageGrantDetailsQuery,
    usePackagesQuery,
    usePaymentHistory,
} from '../../lib/Queries';
import RedeemableForInput from './RedeemableForInput.jsx';
import BookingEventList, {
    EventSummary,
} from '../clients/BookingEventList.jsx';
import useDelete from '../../lib/useDelete.jsx';
import EventDetailsPanel from '../calendar/EventDetailsPanel';
import TextInput from '../TextInput';
import {
    dateStringToDateTime,
    formatDateLong,
    formatDateTime,
    formatInternalDate,
} from '../../lib/DateTime.js';
import Select from '../Select';
import { XCircleIcon } from '@heroicons/react/24/solid';
import {
    humanRedeemableAmount,
    PackageTypes,
    PackageTypeWords,
} from '../../lib/PackageHelpers.js';
import { addDays } from 'date-fns';
import SubmissionDetailsPanel from '../forms/submissions/SubmissionDetailsPanel';
import { ucfirst } from '~/lib/Utils';
import PaymentHistory from '~/components/payments/PaymentHistory';
import { ObjectTypes } from '~/lib/ObjectTypes';
import PaymentList from '~/components/payments/PaymentList';
import SectionHeading from '~/components/SectionHeading';
import InfoMessageBlock from '~/components/InfoMessageBlock';
import DateInput from '~/components/DateInput';
import { formatCurrency } from '~/lib/CurrencyHelpers';
import MoneyInput from '~/components/MoneyInput';
import AuditLogs from '~/components/AuditLogs';
import ClipboardText from '~/components/icons/ClipboardText';
import eventTypeEditPage from '~/pages/business/scheduling/EventTypeEditPage';
import RetrySubscriptionPaymentButton from '~/components/packages/RetrySubscriptionPaymentButton.jsx';
import PackageRedemptionClientText from '~/components/packages/PackageRedemptionClientText.jsx';

function getGrantClient(grant) {
    return {
        ...grant?.client,
        firstName: grant?.client?.first_name,
        lastName: grant?.client?.last_name,
    };
}

const GrantCreatedBy = ({ grant }) => {
    const isAdmin = !grant?.form_submission_id;
    const [showSubmission, setShowSubmission] = useState(false);
    
    return (
        <>
            <div className="panel-label mt-6">Created By</div>
            <div className="panel p-4 flex items-center justify-between">
                <div>
                    <div>
                        {isAdmin && 'Created by an admin'}
                        {!isAdmin && 'Client Form Submission'}
                    </div>
                    <div className="text-sm text-gray-500">
                        {formatDateTime(grant?.created_at)}
                    </div>
                </div>
                {!isAdmin && (
                    <>
                        <button
                            onClick={() => {
                                setShowSubmission(true);
                            }}
                            type="button"
                            className="btn-light-pill-sm uppercase"
                        >
                            View Submission
                        </button>
                    </>
                )}
            </div>
            {!isAdmin && (
                <SubmissionDetailsPanel
                    isOpen={showSubmission}
                    onClose={() => setShowSubmission(false)}
                    refetch={() => null}
                    submission={{
                        submission: { id: grant?.form_submission_id },
                    }}
                />
            )}
        </>
    );
};

const GrantExpiration = ({ grantData, setGrantData, grant, onClose, refetch }) => {
    const [isEditing, setIsEditing] = useState(false);

    const hasRenewal = !!grantData?.next_renewal_at;

    return (
        <>
            <div className="panel-label mt-4">
                {hasRenewal ? 'Renews' : 'Expiration'}
            </div>
            <div className="panel pl-4 py-4">
                {!isEditing && (
                    <div className="flex items-center justify-between pr-4">
                        <div>
                            {!grantData?.expires_at && 'No expiration'}
                            {!!grantData?.expires_at &&
                                formatDateLong(grantData?.expires_at)}
                        </div>
                        <button
                            type="button"
                            className="btn-light-pill-sm uppercase"
                            onClick={() => setIsEditing(!isEditing)}
                        >
                            Edit
                        </button>
                    </div>
                )}
                {isEditing && (
                    <div>
                        <label className="flex items-center space-x-4">
                            <input
                                type="radio"
                                checked={!!grantData?.expires_at}
                                onChange={e =>
                                    e.target.checked &&
                                    setGrantData({
                                        ...grantData,
                                        expires_at:
                                            grant?.expires_at ||
                                            addDays(new Date(), 30),
                                    })
                                }
                            />
                            <div>{hasRenewal ? 'Next renewal' : 'Expires'}</div>
                        </label>
                        {!!grantData?.expires_at && (
                            <DateInput
                                className="ml-6"
                                placeholder="No expiration"
                                autoFocus={true}
                                value={grantData?.expires_at}
                                onChange={newValue => {
                                    setGrantData({
                                        ...grantData,
                                        expires_at: newValue,
                                    });
                                }}
                            />
                        )}
                        <label className="flex items-start space-x-4 mt-4">
                            <input
                                type="radio"
                                className="mt-5"
                                checked={!grantData?.expires_at}
                                onChange={e =>
                                    e.target.checked &&
                                    setGrantData({
                                        ...grantData,
                                        expires_at: '',
                                    })
                                }
                            />
                            <div className="pt-4 border-t flex-1">
                                {hasRenewal
                                    ? 'Pause subscription, do not renew'
                                    : 'Does not expire'}

                                {!grantData?.expires_at && hasRenewal && (
                                    <div className="text-gray-500 text-sm mt-1">
                                        This will not be billed again, but clients can still redeem it
                                    </div>
                                )}
                            </div>
                        </label>

                        {hasRenewal && (
                            <div className="mt-4 border-t pt-4 ml-8">
                                <CancelGrantButton
                                    onClose={onClose}
                                    refetch={refetch}
                                    grantData={grantData}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

const GrantRedemption = ({ grant, grantData, setGrantData, packageData }) => {
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [viewList, setViewList] = useState(false);
    const [editRedeemableFor, setEditRedeemableFor] = useState(false);

    if (typeof grant?.events === 'undefined') {
        // Can't show it! No events means we can't do anything
        return null;
    }

    const isEdited = !!grant?.override_redeemable_for;

    return (
        <>
            <div className="panel-label mt-6">Redemption</div>
            <div className="panel p-4">
                <div className="flex items-center justify-between">
                    <div>
                        This was used for {grant?.events?.length || 0} events.
                        {grant?.events?.length > 0 && !editRedeemableFor && (
                            <button
                                type="button"
                                className="cursor-pointer text-gray-500 underline ml-2"
                                onClick={() => setViewList(!viewList)}
                            >
                                View Events
                            </button>
                        )}
                        <div className="text-gray-500">
                            {humanRedeemableAmount(
                                grant?.redeemable_for?.type,
                                grant?.remaining_balance,
                            )}{' '}
                            remaining.
                        </div>
                        {isEdited && (
                            <div className="text-gray-500 text-sm mt-2">
                                Redeemable amount was edited for this client
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        className="btn-light-pill-sm uppercase"
                        onClick={() => setEditRedeemableFor(!editRedeemableFor)}
                    >
                        Edit
                    </button>
                </div>

                {editRedeemableFor && (
                    <div className="mt-4">
                        <RedeemableForInput
                            value={
                                grantData?.override_redeemable_for ||
                                packageData?.redeemable_for
                            }
                            onChange={newValue =>
                                setGrantData(old => ({
                                    ...old,
                                    override_redeemable_for: newValue,
                                }))
                            }
                        />
                    </div>
                )}

                {!!grant?.events?.length && viewList && (
                    <div className="-mr-4 mt-3">
                        {grant?.events?.map((event, idx) => (
                            <EventSummary
                                event={event}
                                isFirst={false}
                                onClick={() => setSelectedEvent(event)}
                            />
                        ))}

                        <EventDetailsPanel
                            event={selectedEvent}
                            isOpen={!!selectedEvent}
                            onClose={() => setSelectedEvent(null)}
                        />
                    </div>
                )}
            </div>
        </>
    );
};

const NewPackageGrant = ({ grantData, setGrantData, packageData, packageType }) => {
    const { data: allPackagesData } = usePackagesQuery(packageType || '');

    let packageOptions = null;
    if (allPackagesData?.packages) {
        packageOptions = {};
        for (const p of allPackagesData?.packages) {
            packageOptions[p.id] = p.name;
        }
    }

    // Get the label for the dropdown
    const typeLabel = PackageTypeWords[packageType]?.singular || 'package';

    return (
        <div>
            <PackageRedemptionClientText
                client={grantData?.client}
                className="mb-6"
            />

            {!!packageOptions && (
                <Select
                    label={typeLabel}
                    className="mb-6"
                    inputClassName="w-full"
                    value={grantData?.packageId}
                    onChange={newValue =>
                        setGrantData(old => ({ ...old, packageId: newValue }))
                    }
                    options={packageOptions}
                />
            )}

            <div className="panel-label">Assigned to Client</div>
            <ClientPicker
                value={
                    grantData?.client || {
                        firstName: '',
                        lastName: '',
                        email: '',
                    }
                }
                setValue={newValue =>
                    setGrantData({ ...grantData, client: newValue })
                }
            />
        </div>
    );
};

const GrantBilling = ({ grantData, setGrantData, packageData }) => {
    const [isEditingPrice, setIsEditingPrice] = useState(false);

    return (
        <div className="mb-4">
            <div className="panel-label">Billing</div>
            <div className="panel p-4">
                {!isEditingPrice && (
                    <div className="flex items-center justify-between">
                        <div>
                            <div>
                                {formatCurrency(grantData?.price || 0)} each
                                billing cycle
                            </div>
                            {parseFloat(grantData?.price) !==
                                parseFloat(packageData?.price) && (
                                <div className="text-gray-500 text-sm">
                                    This is different than the default of{' '}
                                    {formatCurrency(packageData?.price)}
                                </div>
                            )}
                        </div>
                        <a
                            href="#"
                            className="btn-light-pill-sm uppercase"
                            onClick={e => {
                                e.preventDefault();
                                setIsEditingPrice(!isEditingPrice);
                            }}
                        >
                            Edit
                        </a>
                    </div>
                )}
                {isEditingPrice && (
                    <div className="mt-2 mb-2 flex items-center">
                        <MoneyInput
                            className="max-w-32 mr-2"
                            value={grantData.price}
                            onChange={value =>
                                setGrantData({ ...grantData, price: value })
                            }
                        />
                        each billing cycle
                    </div>
                )}
            </div>
        </div>
    );
};


const CancelGrantButton = ({ className, onClose, refetch, grantData }) => {
    const { saveDelete: saveStopAutorenew, isDeleting: isStoppingAutorenew } = useDelete(
        `/packages/grant/${grantData?.id || '0'}/cancel`,
        {
            data: { cancelImmediately: false },
            prompt: `Are you sure you want to stop autorenewing this? It will remain active until ${grantData?.next_renewal_at ? '*'+formatDateLong(grantData?.next_renewal_at)+'*' : 'the end of the period'} then not renew.`,
        },
    );

    const { saveDelete: saveCancelImmediate, isDeleting: isCancelingImmediately } = useDelete(
        `/packages/grant/${grantData?.id || '0'}/cancel`,
        {
            data: { cancelImmediately: true },
            prompt: `Are you sure you want to cancel this? It will not auto-renew and expire immediately.`,
        },
    );

    function doCancel(isImmediate) {
        let fn = null;
        if (isImmediate) {
            fn = saveCancelImmediate;
        } else {
            fn = saveStopAutorenew;
        }

        fn().then(result => {
            onClose();
            Toast.success(
                'Successfully canceled this subscription, it will no longer renew',
            );
            if (typeof refetch === 'function') {
                refetch();
            }
        });
    }

    return (
        <div className={`flex items-center gap-x-4 ${className}`}>
            {/*Upcoming renewal -- just allow stopping auto-renew*/}
            <button
                type="button"
                className={`btn-pink-pill`}
                onClick={() => doCancel(false)}
            >
                {isStoppingAutorenew ? 'Stopping Auto-Renew...' : 'Stop Auto-Renew'}
            </button>

            <button
                type="button"
                className={`btn-pink-pill`}
                onClick={() => doCancel(true)}
            >
                {isCancelingImmediately ? 'Canceling...' : 'Cancel Immediately'}
            </button>
        </div>
    );
}


const ExistingPackageGrant = ({
    grantData,
    setGrantData,
    packageData,
    grant,
    doDelete,
    isDeleting,
    onClose,
    refetch,
}) => {
    const [showClientPicker, setShowClientPicker] = useState(false);
    const isSubscription =
        grantData?.package?.type === PackageTypes.SUBSCRIPTION;

    return (
        <div>
            {isSubscription &&
                !grantData?.next_renewal_at &&
                !grantData?.is_expired && (
                    <InfoMessageBlock className="mb-4" showIcon={true}>
                        This subscription will not auto-renew{' '}
                        {!!grantData?.expires_at && (
                            <>
                                at the end of this period (
                                {formatDateLong(grantData?.expires_at)})
                            </>
                        )}
                    </InfoMessageBlock>
                )}
            <div className="panel-label">
                {showClientPicker ? 'Transfer To' : 'Assigned To'}
            </div>
            {!showClientPicker && (
                <div className="panel p-4 flex items-center justify-between">
                    <div>
                        <div>
                            {grantData?.client?.first_name}{' '}
                            {grantData?.client?.last_name}
                        </div>
                        <div className="text-gray-500">
                            {grantData?.client?.email || 'Not assigned'}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => {
                            setShowClientPicker(true);
                            setGrantData({ ...grantData, client: {} });
                        }}
                        className="btn-light-pill"
                    >
                        Transfer
                    </button>
                </div>
            )}
            {showClientPicker && (
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                        <ClientPicker
                            value={
                                grantData?.client || {
                                    firstName: '',
                                    lastName: '',
                                    email: '',
                                }
                            }
                            setValue={newValue =>
                                setGrantData({ ...grantData, client: newValue })
                            }
                        />
                    </div>

                    <XCircleIcon
                        className="w-5 h-5 text-gray-500 cursor-pointer mt-2 ml-4"
                        onClick={() => {
                            setShowClientPicker(false);
                            setGrantData({
                                ...grantData,
                                client: getGrantClient(grant),
                            });
                        }}
                    />
                </div>
            )}
            <PackageRedemptionClientText
                client={grantData?.client}
                className="mb-6 text-gray-500 text-sm ml-4 mt-1"
            />

            <GrantRedemption
                grant={grant}
                grantData={grantData}
                setGrantData={setGrantData}
                packageData={packageData}
            />

            <GrantExpiration
                grantData={grantData}
                setGrantData={setGrantData}
                grant={grant}
                onClose={onClose}
                refetch={refetch}
            />

            <GrantCreatedBy grant={grant} />

            {isSubscription && (
                <div className="mt-4">
                    <GrantBilling
                        grantData={grantData}
                        setGrantData={setGrantData}
                        packageData={packageData}
                    />
                    <SectionHeading icon="$" name="Payments" className="mb-2" />
                    <PaymentList
                        type={ObjectTypes.PACKAGE_GRANT}
                        id={grantData?.id}
                        requiresSavedPaymentMethod={isSubscription}
                        renderDetails={({
                            payment,
                            isLast,
                            refetchPayments,
                        }) => (
                            <>
                                {payment.is_failed && isLast && (
                                    <RetrySubscriptionPaymentButton
                                        grantId={grantData?.id}
                                        className="mt-1"
                                        refetch={refetchPayments}
                                    />
                                )}
                            </>
                        )}
                    />
                </div>
            )}

            <SectionHeading
                className="mt-4 mb-2"
                name="Logs"
                icon={<ClipboardText />}
            />
            <AuditLogs type={ObjectTypes.PACKAGE_GRANT} id={grantData?.id} />

            {!!grantData?.next_renewal_at && (
                <CancelGrantButton
                    onClose={onClose}
                    refetch={refetch}
                    grantData={grantData}
                    className="mt-4"
                />
            )}
            {!grantData?.next_renewal_at && (
                <>
                    {/*Only allow deletion if this is canceled*/}
                    <button
                        type="button"
                        className="btn-pink-pill mt-4"
                        onClick={doDelete}
                    >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                </>
            )}
        </div>
    );
};

const EditGrantPanel = ({ isOpen, onClose, grant, packageId, packageType, refetch }) => {
    const isNew = !grant;
    const [grantData, setGrantData] = useState({});
    const { save, isSaving } = useBusinessPostApi(
        `/packages/grant/${grant?.id || '0'}`,
    );
    const { saveDelete, isDeleting } = useDelete(
        `/packages/grant/${grant?.id || '0'}/delete`,
    );

    const shouldFetchDetails = !!grant?.id && grant.id > 0;
    const { data: grantDetailsData, isLoading: isLoadingGrant } = usePackageGrantDetailsQuery(
        shouldFetchDetails ? grant.id : null,
    );
    const fetchedGrant = grantDetailsData?.grant;

    const [isDirty, setIsDirty] = useState(!grant?.id);
    const { data: packageData, isLoading } = usePackageDetailsQuery(
        grantData?.packageId,
    );

    const words = PackageTypeWords[grantData?.package?.type];
    const singular = words?.singular || '';

    useEffect(() => {
        if (!isOpen) {
            setGrantData({
                packageId: grant?.package?.id || packageId,
            });
        }
    }, [isOpen]);

    useEffect(() => {
        if (fetchedGrant) {
            setIsDirty(false);
            setGrantData({
                ...fetchedGrant,
                events: null,
                client: getGrantClient(fetchedGrant),
                packageId: fetchedGrant?.package?.id || packageId,
            });
        } else {
            setIsDirty(!grant?.id);
            setGrantData({
                ...grant,
                events: null,
                client: getGrantClient(grant),
                packageId: grant?.package?.id || packageId,
            });
        }
    }, [JSON.stringify(grant), JSON.stringify(fetchedGrant)]);

    function doDelete() {
        saveDelete().then(result => {
            onClose();
            Toast.success('Successfully deleted');
            if (typeof refetch === 'function') {
                refetch();
            }
        });
    }

    function doSave() {
        save(grantData).then(result => {
            Toast.success(result?.message || 'Successfully saved');
            if (result?.error) {
                Toast.error(result?.error);
            }
            if (typeof refetch === 'function') {
                refetch();
            }
            onClose();
        });
    }

    const resolvedGrant = fetchedGrant || grant;

    return (
        <SidePanel
            isOpen={isOpen}
            onClose={onClose}
            title={
                isNew
                    ? `Add ${ucfirst(singular)} to Client`
                    : `${ucfirst(singular)} Details`
            }
            showSave={isDirty}
            onCancel={onClose}
            onSave={doSave}
            saveLabel={isSaving ? 'Saving...' : 'Save'}
        >
            {isNew && (
                <NewPackageGrant
                    grantData={grantData}
                    setGrantData={setGrantData}
                    packageData={packageData}
                    packageType={packageType}
                />
            )}
            {!isNew && isLoadingGrant && (
                <Loading className="text-center py-8" />
            )}
            {!isNew && !isLoadingGrant && (
                <ExistingPackageGrant
                    grantData={grantData}
                    setGrantData={value => {
                        setGrantData(value);
                        setIsDirty(true);
                    }}
                    packageData={packageData}
                    grant={resolvedGrant}
                    doDelete={doDelete}
                    isDeleting={isDeleting}
                    refetch={refetch}
                    onClose={onClose}
                />
            )}
        </SidePanel>
    );
};

export default EditGrantPanel;
